"use client";

import { useEffect, useRef, useState } from "react";
import { MessageCircle } from "lucide-react";
import LikeButtonBlog from "@/components/LikeButtonBlog";
import Share from "@/components/Share";
import useFixedBarHeight, {
    PAGE_FIXED_FOOTER_HEIGHT_VAR,
} from "@/hooks/useFixedBarHeight";
import useIsDesktop from "@core/helpers/client/hooks/useIsDesktop";

export default function BlogSideActions({ data }) {
    // SSR و hydration هر دو نسخه را دارند؛ بعد از آن فقط نسخه‌ی همان viewport در DOM می‌ماند
    const is_desktop = useIsDesktop();

    const [favorite_counts, set_favorite_counts] = useState(
        data?.favorites_count
    );

    const barRef = useFixedBarHeight(PAGE_FIXED_FOOTER_HEIGHT_VAR);

    const desktop_progress_ref = useRef(null);
    const mobile_progress_ref = useRef(null);

    useEffect(() => {
        let raf = null;
        let last = 0;
        let current = 0;
        let target = 0;
        let dirty = true;

        const measure = () => {
            const article = document.getElementById("blog-article-content");
            if (!article || !article.offsetHeight) return target;

            const rect = article.getBoundingClientRect();
            const scrolled = window.innerHeight * 0.5 - rect.top;

            return Math.min(
                100,
                Math.max(0, (scrolled / article.offsetHeight) * 100)
            );
        };

        const paint = (value) => {
            const scale = value / 100;

            if (desktop_progress_ref.current)
                desktop_progress_ref.current.style.transform = `scaleY(${scale})`;

            if (mobile_progress_ref.current)
                mobile_progress_ref.current.style.transform = `scaleX(${scale})`;
        };

        const tick = (now) => {
            const delta = last ? Math.min(64, now - last) : 16;
            last = now;

            if (dirty) {
                target = measure();
                dirty = false;
            }

            // میرایی نمایی: نرمی انیمیشن مستقل از نرخ فریم نمایشگر
            current += (target - current) * (1 - Math.exp(-delta / 70));

            if (Math.abs(target - current) < 0.05) current = target;

            paint(current);

            if (current === target) {
                raf = null;
                last = 0;
            } else {
                raf = requestAnimationFrame(tick);
            }
        };

        const schedule = () => {
            dirty = true;

            if (raf === null) {
                last = 0;
                raf = requestAnimationFrame(tick);
            }
        };

        schedule();

        window.addEventListener("scroll", schedule, { passive: true });
        window.addEventListener("resize", schedule);

        return () => {
            window.removeEventListener("scroll", schedule);
            window.removeEventListener("resize", schedule);

            if (raf !== null) cancelAnimationFrame(raf);
        };
    }, []);

    const scrollToComments = () => {
        const comments = document.getElementById("comment-tab");

        if (!comments) return;

        const headerHeight =
            parseInt(
                getComputedStyle(document.documentElement).getPropertyValue(
                    "--header-height"
                )
            ) || 0;

        const top =
            comments.getBoundingClientRect().top +
            window.scrollY -
            headerHeight -
            12;

        window.scrollTo({
            top,
            behavior: "smooth",
        });
    };

    return (
        <>
            {/* Desktop */}
            {is_desktop !== false && <div className="hidden lg:flex flex-col items-center min-w-[50px] gap-5 sticky top-[calc(var(--header-height)+12px)] z-10">

                {/* Comment */}
                <button
                    onClick={scrollToComments}
                    className="flex flex-col items-center text-jmt-neutral-600 cursor-pointer gap-1 group"
                    aria-label={`رفتن به نظرات (${data?.comments_count ?? 0})`}
                >
                    <MessageCircle size={22} />

                    <span className="text-[13px]">
                        {data?.comments_count ?? 0}
                    </span>
                </button>

                {/* Like */}
                <div className="flex flex-col items-center gap-1">
                    <LikeButtonBlog
                        data={data}
                        set_favorite_counts={set_favorite_counts}
                    />

                    <span className="text-[11px] font-medium text-neutral-500 tabular-nums">
                        {favorite_counts || 0}
                    </span>
                </div>

                {/* Share */}
                <Share className="text-jmt-neutral-600" />

                {/* Progress bar track */}
                <div className="relative flex flex-col items-center">
                    <div className="w-1 h-25 bg-jmt-neutral-200 rounded-full overflow-hidden relative">
                        <div
                            ref={desktop_progress_ref}
                            className="absolute top-0 left-0 w-full h-full origin-top bg-jmt-primary-800 will-change-transform"
                            style={{ transform: "scaleY(0)" }}
                        />
                    </div>
                </div>
            </div>}

            {/* Mobile */}
            {is_desktop !== true && <div ref={barRef} style={{ bottom: "var(--footer-nav-height, 0px)" }}
                className="lg:hidden fixed inset-x-0 z-40 px-3 pt-3 pb-3 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]">

                {/* Actions container */}
                <div className="relative w-full overflow-hidden rounded-md bg-jmt-neutral-100 p-4">

                    {/* Progress */}
                    <div className="absolute top-0 inset-x-0 h-1 overflow-hidden bg-jmt-neutral-200 z-10">
                        <div
                            ref={mobile_progress_ref}
                            className="absolute top-0 right-0 w-full h-full origin-right bg-jmt-primary-800 will-change-transform"
                            style={{ transform: "scaleX(0)" }}
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-center gap-10">

                        {/* Like */}
                        <div className="w-[25%] flex items-center justify-center gap-1.5 text-jmt-neutral-600">
                            <span className="text-[13px] tabular-nums leading-none">
                                {favorite_counts || 0}
                            </span>

                            <LikeButtonBlog
                                data={data}
                                set_favorite_counts={set_favorite_counts}
                                className="flex items-center justify-center"
                                icon_class_name="w-6 h-6"
                            />
                        </div>

                        {/* Comment */}
                        <button
                            onClick={scrollToComments}
                            className="w-[25%] flex items-center justify-center gap-1.5 text-jmt-neutral-600 cursor-pointer"
                            aria-label={`رفتن به نظرات (${data?.comments_count ?? 0})`}
                        >
                            <span className="text-[13px] tabular-nums leading-none">
                                {data?.comments_count ?? 0}
                            </span>

                            <MessageCircle className="w-6 h-6" />
                        </button>

                        {/* Share */}
                        <div className="w-[25%] flex items-center justify-center text-jmt-neutral-600">
                            <Share
                                className="flex items-center justify-center"
                                mobile_icon_size={24}
                            />
                        </div>
                    </div>
                </div>
            </div>}
        </>
    );
}

